<template>
  <div class="rich-offer-content">
    <!--
      Compound key = segment kind + its ordinal position within its kind.
      Using just `index` forced Vue to reuse the wrong component instance
      when a marker was added/removed upstream (CTA components are more
      sensitive than plain HTML blocks), which could wire the wrong
      `choiceId` through to CheckoutButtonProcessOrder.
    -->
    <template v-for="segment in keyedSegments" :key="segment._key">
      <div
        v-if="segment.kind === 'html'"
        class="rich-offer-html"
        v-html="segment.value"
      ></div>
      <CheckoutButtonProcessOrder
        v-else-if="segment.kind === 'cta'"
        :item="fillProductWithChoice(product)"
        :step="step"
        :type="orderType"
        :processordertext="segment.buttonHtml"
        variant="plain"
      />
    </template>
  </div>
</template>

<script>
import CheckoutButtonProcessOrder from "@/components/checkout/ButtonProcessOrder.vue";
import { parseRichOfferContent } from "@/services/richOfferParser";
import { extractTrustedScripts } from "@/services/scriptOriginAllowList";

/**
 * RichOfferContent (FORGE-264, FORGE-400)
 *
 * Renders an upsell/cross-sell offer stored as a single rich-HTML string
 * (OrderPageItemChoice__c.Argument1__c). The HTML may contain zero or more
 * CTA markers of the form:
 *
 *   <div class="op-cta" data-choice-id="<OrderPageItemChoice__c.Id>">
 *     <span class="op-cta-button" style="...">Label text</span>
 *   </div>
 *
 * Each marker is replaced in-place by a functional <CheckoutButtonProcessOrder>.
 * The rest of the HTML is rendered as-is via v-html so marketers keep full
 * control over the layout (product name, image, price, legal notice, etc.).
 *
 * FORGE-400 — script injection:
 *   `v-html` does NOT execute embedded `<script>` tags (DOM standard). To
 *   support trusted external embeds (Vidyard player, Calendly, GTM, …),
 *   `mounted()` extracts trusted-origin scripts via
 *   `scriptOriginAllowList.extractTrustedScripts` and re-creates them as
 *   real `document.createElement('script')` elements appended to the
 *   component's root. They are removed in `unmounted()`. The `html` prop is
 *   watched so a dynamic update (parent passes a new value while the
 *   component stays mounted) re-runs the inject/cleanup cycle. Untrusted
 *   scripts were already stripped server-side in
 *   `OrderPageConfigController.sanitizeScripts`; we re-validate here as
 *   defence-in-depth.
 *
 * Caveats:
 *   - SSR: injectTrustedScripts() guards `typeof document` so SSR builds are
 *     no-op. Scripts are injected at hydration time on the client.
 *   - <keep-alive>: this component is NOT designed to be wrapped in
 *     <keep-alive>. With keep-alive, mounted() runs only on the first
 *     activation and unmounted() only on the last deactivation, so
 *     activated/deactivated cycles would leave injected scripts attached
 *     to a hidden DOM tree. Wrap the parent route or remove keep-alive
 *     before reusing this component.
 */
export default {
  name: "RichOfferContent",
  components: { CheckoutButtonProcessOrder },
  props: {
    html: {
      type: String,
      default: "",
    },
    product: {
      type: Object,
      required: true,
    },
    step: {
      type: Number,
      default: 0,
    },
    orderType: {
      type: String,
      default: "crosssell",
    },
    fillProductWithChoice: {
      type: Function,
      required: true,
    },
    defaultChoiceId: {
      type: String,
      default: "",
    },
  },
  computed: {
    segments() {
      // Inject the i18n-localized fallback label for CTA markers whose inner
      // span is missing — this keeps the parser pure and locale-agnostic.
      return parseRichOfferContent(this.html || "", this.defaultChoiceId, {
        fallbackButtonLabel: this.$t("labels.ctaDefault") || "",
      });
    },
    keyedSegments() {
      let htmlIdx = 0;
      let ctaIdx = 0;
      return this.segments.map((segment) => {
        const key =
          segment.kind === "cta"
            ? `cta:${segment.choiceId || ""}:${ctaIdx++}`
            : `html:${htmlIdx++}`;
        return { ...segment, _key: key };
      });
    },
  },
  created() {
    // Track injected scripts so we can remove them on unmount. Stored as a
    // PLAIN instance property (not in data()) for two reasons:
    //   1. DOM nodes have no business in Vue's reactivity system; making them
    //      reactive would force devtools to walk them on snapshots.
    //   2. Vue 3 does NOT proxy data() keys starting with `_` or `$` onto
    //      `this` (per Vue docs: those are reserved for internal use).
    //      Declaring `_injectedScripts` in data() would silently make
    //      `this._injectedScripts` undefined and crash unmounted().
    this.injectedScripts = [];
  },
  mounted() {
    // Defense-in-depth: a thrown exception in mounted() does NOT blank the
    // initial DOM (Vue mounts the template first), but it pollutes Sentry
    // and breaks subsequent re-renders. Same for unmounted(). Wrap so any
    // future regression in extractTrustedScripts / DOM access stays local.
    this.safeInjectTrustedScripts();
  },
  unmounted() {
    this.safeRemoveInjectedScripts();
  },
  watch: {
    // Pubfac PR #1279: cover the dynamic-prop path. If the parent re-renders
    // the same RichOfferContent instance with a different `html` value
    // (e.g. cart updates the cross-sell payload), the previously injected
    // scripts must be detached and the new ones injected. Without this
    // watcher, mounted() ran only once and the new content would be silently
    // missing its scripts.
    html() {
      this.safeRemoveInjectedScripts();
      this.safeInjectTrustedScripts();
    },
  },
  methods: {
    safeInjectTrustedScripts() {
      try {
        this.injectTrustedScripts();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[RichOfferContent] injectTrustedScripts failed", e);
      }
    },
    safeRemoveInjectedScripts() {
      try {
        this.removeInjectedScripts();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[RichOfferContent] removeInjectedScripts failed", e);
      }
    },
    injectTrustedScripts() {
      // SSR guard: on the server, `document` is undefined. The component is
      // expected to inject scripts at hydration time on the client, so the
      // SSR pass is a no-op rather than a try/catch absorbed crash.
      if (typeof document === "undefined") return;

      const descriptors = extractTrustedScripts(this.html || "");
      if (descriptors.length === 0) return;

      // Anchor the scripts on the component's root rather than document.head
      // so they are torn down cleanly with the component (and so multiple
      // RichOfferContent instances on the same page each manage their own
      // lifecycle without racing each other for global script state).
      // Vidyard's embed/v4.js scans the whole document for `.vidyard-player-embed`
      // images regardless of where it is loaded from, so attaching to $el is
      // functionally equivalent to <head> for the player to find its DOM
      // anchor — but we keep cleanup deterministic.
      const host = this.$el;
      if (!host) return;

      for (const d of descriptors) {
        const script = document.createElement("script");
        script.src = d.src;
        if (d.async) script.async = true;
        if (d.defer) script.defer = true;
        if (d.type) script.type = d.type;
        if (d.crossorigin) script.crossOrigin = d.crossorigin;
        host.appendChild(script);
        this.injectedScripts.push(script);
      }
    },
    removeInjectedScripts() {
      if (!Array.isArray(this.injectedScripts)) return;
      for (const s of this.injectedScripts) {
        if (s && s.parentNode) {
          s.parentNode.removeChild(s);
        }
      }
      this.injectedScripts = [];
    },
  },
};
</script>

<style scoped>
.rich-offer-content {
  display: block;
  width: 100%;
}
.rich-offer-content .rich-offer-html {
  width: 100%;
}
</style>
